/**
 * F14.3 — Componentes compartilhados pelas 4 telas auth (Login,
 * ForgotPassword, ResetPassword, AcceptInvite). Todos consomem tokens
 * `var(--*)` do design system v2.1.
 *
 *   <AuthShell>                  → background navy + LanguageSwitcher
 *     <AuthBrand subtitle={...}> → logo SVG + subtítulo
 *     <FieldLabel>               → label uppercase lime
 *     <PillInput>                → input pill com glow lime no focus
 *     <PrimaryButton>            → CTA full-width 56px lime pill
 *     <AlertError>               → padrão Alert do /design-system §9
 *     <AlertSuccess>             → variante success
 *     <GhostLink>                → link text-secondary → lime no hover
 */

import { useState, type ReactNode } from "react";
import { Link } from "wouter";
import { LanguageSwitcher } from "../common/LanguageSwitcher.js";
import { useT } from "../../i18n/index.js";

/**
 * F-auth-fixed-colors (2026-09-10, reporte do CEO no celular) — as telas de
 * auth têm fundo navy CONSTANTE, mas usavam tokens de texto TEMATIZADOS:
 * no tema claro, --text-primary é azul-escuro → digitação invisível sobre o
 * navy (inclusive a senha revelada). Cores de texto aqui são FIXAS.
 */
const AUTH_TEXT = "#f4f7fc";
const AUTH_TEXT_SECONDARY = "rgba(244, 247, 252, 0.72)";
const AUTH_TEXT_MUTED = "rgba(244, 247, 252, 0.45)";

/* ─── Shell ─────────────────────────────────────────────────────── */

export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-10 relative"
      style={{ background: "var(--navy)", color: AUTH_TEXT }}
    >
      <div className="absolute top-5 right-5">
        <LanguageSwitcher />
      </div>
      {children}
    </div>
  );
}

/* ─── Brand / Hero ──────────────────────────────────────────────── */

export function AuthBrand({ subtitle }: { subtitle: string }) {
  return (
    <div className="text-center mb-10">
      <img
        src="/euro-storm-logo.svg"
        alt="Euro Storm Débosselage"
        className="mx-auto block mb-5"
        style={{
          width: "100%",
          maxWidth: 320,
          height: "auto",
          filter: "drop-shadow(0 0 24px rgba(184, 245, 123, 0.15))",
        }}
      />
      <p
        style={{
          fontFamily: "var(--font-body)",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: "2.5px",
          textTransform: "uppercase",
          color: AUTH_TEXT_MUTED,
        }}
      >
        {subtitle}
      </p>
    </div>
  );
}

/* ─── FieldLabel ────────────────────────────────────────────────── */

export function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block mb-2"
      style={{
        fontFamily: "var(--font-body)",
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: "2px",
        textTransform: "uppercase",
        color: "var(--lime)",
      }}
    >
      {children}
    </label>
  );
}

/* ─── PillInput ─────────────────────────────────────────────────── */

interface PillInputProps {
  type?: "email" | "password" | "text";
  value: string;
  onChange: (v: string) => void;
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  placeholder?: string;
  id?: string;
  minLength?: number;
}

export function PillInput(props: PillInputProps) {
  const t = useT();
  const [reveal, setReveal] = useState(false);
  const isPassword = props.type === "password";
  const inputType = isPassword && reveal ? "text" : (props.type ?? "text");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "0 22px",
        height: 56,
        background: "var(--navy-soft)",
        borderRadius: "var(--radius-pill)",
        border: `1.5px solid ${props.focused ? "var(--lime)" : "var(--navy-elevated)"}`,
        boxShadow: props.focused ? "var(--shadow-lime-glow)" : "none",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        opacity: props.disabled ? 0.6 : 1,
      }}
    >
      <input
        className="auth-pill-input"
        id={props.id}
        type={inputType}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        disabled={props.disabled}
        required={props.required}
        autoFocus={props.autoFocus}
        autoComplete={props.autoComplete}
        placeholder={props.placeholder}
        minLength={props.minLength}
        style={{
          flex: 1,
          background: "transparent",
          border: "none",
          outline: "none",
          color: AUTH_TEXT,
          fontFamily: "var(--font-body)",
          fontSize: 15,
          height: "100%",
        }}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setReveal((r) => !r)}
          tabIndex={-1}
          aria-label={reveal ? t("common.hidePassword") : t("common.showPassword")}
          title={reveal ? t("common.hidePassword") : t("common.showPassword")}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            opacity: 0.7,
            paddingLeft: 8,
          }}
        >
          {reveal ? "🙈" : "👁️"}
        </button>
      )}
    </div>
  );
}

/* ─── PrimaryButton (CTA pill 56px) ─────────────────────────────── */

interface PrimaryButtonProps {
  children: ReactNode;
  type?: "button" | "submit";
  disabled?: boolean;
  onClick?: () => void;
  /** Texto em uppercase quando true. Default `true`. */
  uppercase?: boolean;
}

export function PrimaryButton({
  children,
  type = "submit",
  disabled,
  onClick,
  uppercase = true,
}: PrimaryButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className="w-full"
      style={{
        height: 56,
        paddingLeft: 28,
        paddingRight: 28,
        fontSize: 16,
        fontFamily: "var(--font-body)",
        fontWeight: 800,
        background: disabled ? "var(--lime-soft)" : "var(--lime)",
        color: disabled ? AUTH_TEXT_MUTED : "var(--text-on-lime)",
        borderRadius: "var(--radius-pill)",
        boxShadow: disabled ? "none" : "var(--shadow-lime-glow)",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        letterSpacing: "1px",
        textTransform: uppercase ? "uppercase" : "none",
        transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = "var(--lime-bright)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = "var(--lime)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {children}
    </button>
  );
}

/* ─── AlertError / AlertSuccess (pattern §9 do /design-system) ──── */

export function AlertError({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "12px 14px",
        borderRadius: "var(--radius-sm)",
        background: "rgba(208, 72, 72, 0.08)",
        borderLeft: "3px solid var(--danger)",
      }}
    >
      <span
        style={{ color: "var(--danger)", fontWeight: 700, fontSize: 16, lineHeight: 1 }}
        aria-hidden
      >
        ✕
      </span>
      <p
        style={{
          fontFamily: "var(--font-body)",
          fontSize: 13,
          color: AUTH_TEXT_SECONDARY,
          lineHeight: 1.4,
        }}
      >
        {children}
      </p>
    </div>
  );
}

export function AlertSuccess({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "12px 14px",
        borderRadius: "var(--radius-sm)",
        background: "rgba(61, 165, 97, 0.08)",
        borderLeft: "3px solid var(--success)",
      }}
    >
      <span
        style={{ color: "var(--success)", fontWeight: 700, fontSize: 16, lineHeight: 1 }}
        aria-hidden
      >
        ✓
      </span>
      <p
        style={{
          fontFamily: "var(--font-body)",
          fontSize: 13,
          color: AUTH_TEXT_SECONDARY,
          lineHeight: 1.4,
        }}
      >
        {children}
      </p>
    </div>
  );
}

/* ─── GhostLink ─────────────────────────────────────────────────── */

export function GhostLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <p
      className="text-center"
      style={{
        fontFamily: "var(--font-body)",
        fontSize: 13,
        color: AUTH_TEXT_MUTED,
      }}
    >
      <Link
        href={href}
        style={{ color: AUTH_TEXT_SECONDARY, textDecoration: "none" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--lime)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = AUTH_TEXT_SECONDARY)}
      >
        {children}
      </Link>
    </p>
  );
}
